import LinearProgress from "@mui/material/LinearProgress";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

// The submission's processing bar (BRK-S488; Nav, 2026-10-28: "processing bar
// must be global so that we can use it in other line of business"). ONE thin
// bar along the bottom edge of the top navbar, while any part of the page has
// registered work through `useProgress`. It belongs to the submission: the
// base wizard drives it while it reads or saves the application, and every
// step placeholder (`StepLoading`) while it waits, whatever the line of
// business. Lists keep their own bar and the quotes loading screen its own.
// It is decoration: the status a screen reader hears is the placeholder's or
// the button's ("Saving…"). Under reduced motion it goes. Like the breadcrumb
// (PageTrail.tsx), registering and showing are separate contexts, so the parts
// that work don't re-render as the count moves.

const StartContext = createContext<(() => () => void) | null>(null);
const ShownContext = createContext(false);

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [working, setWorking] = useState(0);
  const start = useCallback(() => {
    setWorking((n) => n + 1);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      setWorking((n) => n - 1);
    };
  }, []);
  return (
    <StartContext.Provider value={start}>
      <ShownContext.Provider value={working > 0}>
        {children}
      </ShownContext.Provider>
    </StartContext.Provider>
  );
}

/** Runs the global bar while `active`, until the caller unmounts. Outside the
 *  portal's shell it does nothing. */
// eslint-disable-next-line react-refresh/only-export-components
export function useProgress(active: boolean): void {
  const start = useContext(StartContext);
  useEffect(() => {
    if (!active || !start) return;
    return start();
  }, [active, start]);
}

/** The bar itself, on the top navbar's bottom edge (the header is its
 *  positioned parent). */
export function TopProgressBar() {
  const shown = useContext(ShownContext);
  return (
    <div
      data-top-progress
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 -bottom-px motion-reduce:hidden"
    >
      {shown && <LinearProgress sx={{ height: 3 }} />}
    </div>
  );
}
